import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import EssenceManagement from '../components/EssenceManagement';
import ListsManagement from '../components/ListsManagement';
import PlanningManagement from '../components/PlanningManagement';
import { BillingDashboard } from '../components/billing/BillingDashboard';
import { BillingDashboardV2 } from '../components/billing/v2/BillingDashboardV2';
import { useOrganization } from '../context/OrganizationContext';
import ManageTeam from './ManageTeam';
import { API_URL } from '../constants/urls';
interface OrganizationData {
  id: string;
  name: string;
  description?: string;
  geographic_markets?: string[] | null;
  currency: string;
  research_context_notes?: string;
}

const OrganizationManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedOrg: organization,
    userRole: orgRole,
    refreshOrganizations,
  } = useOrganization();
  const organizationId = organization?.id || '';
  const onOrganizationUpdate = refreshOrganizations;
  const [searchParams, setSearchParams] = useSearchParams();

  // Function to get initial tab from URL hash
  const getInitialTab = ():
    | 'details'
    | 'team'
    | 'essence'
    | 'planning'
    | 'lists'
    | 'billing' => {
    const hash = location.hash.replace('#', '');
    if (
      hash === 'team' ||
      hash === 'essence' ||
      hash === 'planning' ||
      hash === 'lists' ||
      hash === 'billing'
    ) {
      return hash as 'team' | 'essence' | 'planning' | 'lists' | 'billing';
    }
    return 'details';
  };

  const [activeTab, setActiveTab] = useState<
    'details' | 'team' | 'essence' | 'planning' | 'lists' | 'billing'
  >(getInitialTab());
  const [orgData, setOrgData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [geographicMarkets, setGeographicMarkets] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [researchContextNotes, setResearchContextNotes] = useState('');

  // Delete organization state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Billing UI version toggle
  const [useBillingV2, setUseBillingV2] = useState(false);

  const isAdmin = orgRole === 'admin';

  // Function to handle tab changes and update URL
  const handleTabChange = (
    tab: 'details' | 'team' | 'essence' | 'planning' | 'lists' | 'billing',
  ) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (
        hash === 'team' ||
        hash === 'essence' ||
        hash === 'planning' ||
        hash === 'lists' ||
        hash === 'billing'
      ) {
        setActiveTab(
          hash as 'team' | 'essence' | 'planning' | 'lists' | 'billing',
        );
      } else if (hash === 'details' || !hash) {
        setActiveTab('details');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const statusParam = searchParams.get('status');
    const newOrgParam = searchParams.get('new_org');

    if (
      tabParam &&
      ['details', 'team', 'essence', 'planning', 'lists', 'billing'].includes(
        tabParam,
      )
    ) {
      // Only allow billing tab if user is admin
      if (tabParam === 'billing' && orgRole !== 'admin') {
        setActiveTab('details');
      } else {
        setActiveTab(
          tabParam as
            | 'details'
            | 'team'
            | 'essence'
            | 'planning'
            | 'lists'
            | 'billing',
        );
      }
    }

    // Show success message for new organization creation
    if (statusParam === 'success' && newOrgParam === 'true') {
      setSuccess('Organization created successfully with your selected plan!');
      // Clean up URL parameters
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      newParams.delete('new_org');
      setSearchParams(newParams);
    }

    // Handle status parameter for payment redirects
    if (statusParam) {
      if (statusParam === 'success') {
        setSuccess('Payment successful! Your subscription has been updated.');
      } else if (statusParam === 'cancelled') {
        setError(
          'Payment was cancelled. Your subscription has not been changed.',
        );
      }

      // Remove status from URL after displaying message
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, orgRole, setSearchParams]);
  const canView = orgRole
    ? ['admin', 'editor', 'viewer'].includes(orgRole)
    : false;

  useEffect(() => {
    if (organizationId && canView) {
      fetchOrganizationData();
    }
  }, [organizationId]);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}`,
      );
      const data = response.data;
      setOrgData(data);
      setName(data.name || '');
      setDescription(data.description || '');
      // Convert array to comma-separated string for display
      setGeographicMarkets(
        Array.isArray(data.geographic_markets)
          ? data.geographic_markets.join(', ')
          : data.geographic_markets || '',
      );
      setCurrency(data.currency || 'USD');
      setResearchContextNotes(data.research_context_notes || '');
    } catch (err: any) {
      setError('Failed to fetch organization data');
      // console.error removed
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Convert geographic markets to array if it's a string
      const marketsArray = geographicMarkets
        ? geographicMarkets
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : null;

      await axios.put(`${API_URL}/organizations/${organizationId}`, {
        name,
        description,
        geographic_markets: marketsArray,
        currency,
        research_context_notes: researchContextNotes,
      });

      // Update the local orgData state with the new values
      setOrgData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          name,
          description,
          geographic_markets: marketsArray,
          currency,
          research_context_notes: researchContextNotes,
        };
      });

      setSuccess('Organization settings updated successfully');
      if (onOrganizationUpdate) {
        onOrganizationUpdate();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!isAdmin || !orgData) return;

    // Check if user typed the org name correctly
    if (deleteConfirmText !== orgData.name) {
      setError(
        'Organization name does not match. Please type the exact name to confirm deletion.',
      );
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await axios.delete(`${API_URL}/organizations/${organizationId}`);

      // Close dialog
      setShowDeleteDialog(false);

      // Refresh organizations list
      await refreshOrganizations();

      // Navigate to home page after deletion
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  if (!canView) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Alert severity="error">
          You do not have permission to access Organization Management.
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <Typography variant="h4" className="mb-2 text-2xl sm:text-3xl">
          Organization Management
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          className="text-sm sm:text-base"
        >
          {isAdmin
            ? 'Manage your organization settings and team members'
            : 'View your organization details and team members'}
        </Typography>
      </div>

      {/* User Role Display */}
      <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2">
        <Chip
          label={`Your role: ${orgRole ? orgRole.charAt(0).toUpperCase() + orgRole.slice(1) : 'Unknown'}`}
          color={
            isAdmin ? 'primary' : orgRole === 'editor' ? 'secondary' : 'default'
          }
          size="small"
        />
        {!isAdmin && (
          <Typography
            variant="body2"
            color="text.secondary"
            className="text-xs sm:text-sm"
          >
            {orgRole === 'editor'
              ? 'You can view organization details and team members.'
              : 'You have read-only access to organization information.'}
          </Typography>
        )}
      </div>

      {/* Mobile Tab Navigation */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6">
        <nav className="-mb-px flex space-x-4 sm:space-x-8" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('details')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
              ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <BuildingOfficeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Organization Details</span>
            <span className="sm:hidden">Details</span>
          </button>
          <button
            onClick={() => handleTabChange('team')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
              ${
                activeTab === 'team'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Team Members</span>
            <span className="sm:hidden">Team</span>
          </button>
          <button
            onClick={() => handleTabChange('essence')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
              ${
                activeTab === 'essence'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Organization Essence</span>
            <span className="sm:hidden">Essence</span>
          </button>
          <button
            onClick={() => handleTabChange('planning')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
              ${
                activeTab === 'planning'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Planning Cycles</span>
            <span className="sm:hidden">Planning</span>
          </button>
          <button
            onClick={() => handleTabChange('lists')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
              ${
                activeTab === 'lists'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <ListBulletIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Lists & Selectors</span>
            <span className="sm:hidden">Lists</span>
          </button>
          {orgRole === 'admin' && (
            <button
              onClick={() => handleTabChange('billing')}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2
                ${
                  activeTab === 'billing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <CreditCardIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Billing & Plans</span>
              <span className="sm:hidden">Billing</span>
            </button>
          )}
        </nav>
      </div>

      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          className="mb-4"
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Tab Content */}
      <div className={activeTab === 'details' ? 'block' : 'hidden'}>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <Typography variant="h6" className="mb-4 text-lg sm:text-xl">
              Organization Details
            </Typography>
            <Divider className="mb-4 sm:mb-6" />

            {loading ? (
              <Typography>Loading organization data...</Typography>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isAdmin}
                    helperText={
                      !isAdmin
                        ? 'Only admins can change the organization name'
                        : ''
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!isAdmin}
                    helperText={
                      isAdmin
                        ? "Describe your organization's mission and focus"
                        : undefined
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Geographic Markets"
                    value={geographicMarkets}
                    onChange={(e) => setGeographicMarkets(e.target.value)}
                    disabled={!isAdmin}
                    helperText={
                      isAdmin
                        ? 'Comma-separated list (e.g., US, UK, EU)'
                        : undefined
                    }
                    size="small"
                  />

                  <FormControl fullWidth disabled={!isAdmin} size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={currency}
                      label="Currency"
                      onChange={(e: SelectChangeEvent) =>
                        setCurrency(e.target.value)
                      }
                    >
                      <MenuItem value="USD">USD - US Dollar</MenuItem>
                      <MenuItem value="GBP">GBP - British Pound</MenuItem>
                      <MenuItem value="EUR">EUR - Euro</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Research Context Notes"
                    value={researchContextNotes}
                    onChange={(e) => setResearchContextNotes(e.target.value)}
                    disabled={!isAdmin}
                    helperText={
                      isAdmin
                        ? 'Additional context for AI research assistant'
                        : undefined
                    }
                    size="small"
                  />

                  {isAdmin && (
                    <div className="pt-4 flex justify-end">
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        size="medium"
                        className="w-full sm:w-auto"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone - Only for admins */}
        {isAdmin && (
          <Card className="mt-6 border-red-200 bg-red-50">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                <Typography variant="h6" className="text-red-800 text-lg">
                  Danger Zone
                </Typography>
              </div>
              <Divider className="mb-4" />

              <div className="space-y-4">
                <div>
                  <Typography
                    variant="subtitle1"
                    className="text-red-800 font-medium mb-2"
                  >
                    Delete Organization
                  </Typography>
                  <Typography variant="body2" className="text-red-700 mb-4">
                    Permanently delete this organization and all its data. This
                    action cannot be undone.
                  </Typography>

                  <div className="bg-red-100 p-3 rounded-md mb-4">
                    <Typography
                      variant="body2"
                      className="text-red-800 font-medium mb-2"
                    >
                      This will permanently delete:
                    </Typography>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• All segments, propositions, and companies</li>
                      <li>• All opportunities and contacts</li>
                      <li>• All team members and their access</li>
                      <li>• All research chat sessions and data</li>
                      <li>• All planning cycles and OKRs</li>
                      <li>• Active billing subscriptions will be cancelled</li>
                    </ul>
                  </div>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full sm:w-auto"
                    startIcon={<ExclamationTriangleIcon className="h-4 w-4" />}
                  >
                    Delete Organization
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className={activeTab === 'team' ? 'block' : 'hidden'}>
        <ManageTeam
          organization={organization}
          isReadOnly={!isAdmin}
          userRole={orgRole || 'viewer'}
        />
      </div>

      <div className={activeTab === 'essence' ? 'block' : 'hidden'}>
        <EssenceManagement
          organizationId={organizationId}
          orgRole={orgRole || 'viewer'}
          organization={orgData || organization}
        />
      </div>

      <div className={activeTab === 'planning' ? 'block' : 'hidden'}>
        <PlanningManagement
          organizationId={organizationId}
          orgRole={orgRole || 'viewer'}
          organization={orgData || organization}
        />
      </div>

      <div className={activeTab === 'lists' ? 'block' : 'hidden'}>
        <ListsManagement
          organizationId={organizationId}
          orgRole={orgRole || 'viewer'}
          organization={orgData || organization}
        />
      </div>

      {orgRole === 'admin' && (
        <div className={activeTab === 'billing' ? 'block' : 'hidden'}>
          {/* Version Toggle */}
          <div className="mb-4 flex justify-end">
            <div className="bg-gray-100 p-1 rounded-lg inline-flex">
              <button
                onClick={() => setUseBillingV2(false)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  !useBillingV2
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Original
              </button>
              <button
                onClick={() => setUseBillingV2(true)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  useBillingV2
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                V2 (New)
              </button>
            </div>
          </div>

          {/* Conditional Rendering */}
          {useBillingV2 ? <BillingDashboardV2 /> : <BillingDashboard />}
        </div>
      )}

      {/* Delete Organization Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeleteConfirmText('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="text-red-800">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6" />
            Delete Organization
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            <Alert severity="error" className="mb-4">
              <strong>Warning:</strong> This action cannot be undone. All data
              will be permanently deleted.
            </Alert>

            <Typography variant="body1" className="mb-4">
              You are about to permanently delete{' '}
              <strong>{orgData?.name}</strong> and all its data.
            </Typography>

            <div className="bg-red-50 p-3 rounded-md">
              <Typography
                variant="body2"
                className="text-red-800 font-medium mb-2"
              >
                This will permanently delete:
              </Typography>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• All segments, propositions, and companies</li>
                <li>• All opportunities and contacts</li>
                <li>• All team members and their access</li>
                <li>• All research chat sessions and data</li>
                <li>• All planning cycles and OKRs</li>
                <li>• Active billing subscriptions will be cancelled</li>
              </ul>
            </div>

            <div className="pt-4">
              <Typography variant="body2" className="mb-2">
                To confirm deletion, type the organization name:{' '}
                <strong>{orgData?.name}</strong>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`Type "${orgData?.name}" to confirm`}
                size="small"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteDialog(false);
              setDeleteConfirmText('');
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrganization}
            color="error"
            variant="contained"
            disabled={deleting || deleteConfirmText !== orgData?.name}
          >
            {deleting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrganizationManagement;
